import type { VariantTags } from "@/platform/thecore/ingest";
import {
  type AxisKey,
  axisOptionLabel,
  DIMENSIONS,
  type EditionAxis,
  editionAxes,
  facet,
  facetValues,
} from "./index";

/**
 * One faceted setup the Setup picker offers: a selectable id + display name, its family (the variant
 * `source`, e.g. "TheCore" / "Blizzard") and its Variant tags. This is the "entries that carry a
 * variant" shape the picker facets over - TheCore catalog Presets plus the Blizzard stock presets,
 * unified so the same faceting machinery (editionAxes / facet) drives both. Uploads and Personal
 * setups have no variant and are handled as flat families, not through this model.
 */
export interface FacetedSetup {
  id: string;
  name: string;
  source: string;
  variant: VariantTags;
}

/** A flat (non-faceted) member: an upload or a Personal setup, chosen by id, not by variant. */
export interface FlatMember {
  id: string;
  name: string;
}

/** The display label for one axis value (Title-case, with the catalog's P/T/R special case). Widened to
 * a string axis key so the picker can label a segmented control without narrowing to AxisKey first. */
export function axisOptionLabelOf(axis: string, value: string): string {
  return axisOptionLabel(axis as AxisKey, value);
}

/** The setups belonging to one faceted family, selected by their Variant `source`. */
export function familyOf<T extends { source: string }>(setups: readonly T[], source: string): T[] {
  return setups.filter((s) => s.source === source);
}

/** A family's edition tokens, in first-seen order (the picker orders them for display per family). */
export function editionsOf(setups: readonly FacetedSetup[]): string[] {
  return facetValues(setups).edition;
}

/**
 * The axis controls to render for an edition: the edition's declared axes (from editionAxes) with
 * single-option axes dropped - they are auto-applied, nothing to choose - EXCEPT hand, which always
 * renders so a setup's resting hand is always visible. Order follows VARIANT_AXES (editionAxes omits
 * the edition axis itself).
 */
export function axesOf(setups: readonly FacetedSetup[], edition: string): EditionAxis[] {
  const declared = editionAxes(setups).get(edition) ?? [];
  return declared.filter((axis) => axis.key === "hand" || axis.options.length > 1);
}

/**
 * Reconcile axis values when the edition changes: for each axis the new edition declares, keep the
 * previous value if that edition still offers it, else fall back to the axis's first option. Axes the
 * new edition does not declare drop away. Mirrors validRosterId / validContextId - the raw selection
 * is preserved by the caller and only rendered valid here - so every reachable combination resolves to
 * a real entry. Returns the full VariantTags naming exactly one setup.
 */
export function reconcileVariant(
  setups: readonly FacetedSetup[],
  edition: string,
  prev: Partial<VariantTags>,
): VariantTags {
  const declared = editionAxes(setups).get(edition) ?? [];
  const out: VariantTags = { edition, hand: "" };
  for (const axis of declared) {
    const wanted = prev[axis.key];
    out[axis.key] = wanted && axis.options.includes(wanted) ? wanted : (axis.options[0] ?? "");
  }
  return out;
}

/** The id of the single setup a fully-specified variant names (via facet), or undefined if none. */
export function resolveId(
  setups: readonly FacetedSetup[],
  variant: Partial<VariantTags>,
): string | undefined {
  return facet(setups, variant)[0]?.id;
}

/** A committed id back to its family + variant, so the picker can seed its draft from the selection. */
export function locate(
  setups: readonly FacetedSetup[],
  id: string,
): { source: string; variant: VariantTags } | undefined {
  const found = setups.find((s) => s.id === id);
  return found ? { source: found.source, variant: found.variant } : undefined;
}

/** The trigger face for a faceted setup: family+edition (bold main), non-hand axes as pills, hand apart. */
export interface TriggerLabel {
  main: string;
  pills: string[];
  hand?: string;
}

/**
 * The trigger label parts for a setup: `<family> <edition>` as the bold main, each remaining set axis
 * (race / size / modifier) as a pill, and hand rendered last and accented. Derived from the same
 * per-axis labels as variantTagLabels, so the closed trigger reads the selection faithfully.
 */
export function triggerLabel(familyName: string, variant: VariantTags): TriggerLabel {
  const pills: string[] = [];
  for (const axis of DIMENSIONS) {
    if (axis === "edition" || axis === "hand") continue;
    const value = variant[axis];
    if (value) pills.push(axisOptionLabel(axis, value));
  }
  return {
    main: `${familyName} ${axisOptionLabel("edition", variant.edition)}`,
    pills,
    hand: variant.hand ? axisOptionLabel("hand", variant.hand) : undefined,
  };
}

/** One search hit: the selectable id, its display name, and the matched variant tokens (for display). */
export interface SearchHit {
  id: string;
  name: string;
  tags: string[];
}

const AXIS_KEYS: readonly AxisKey[] = DIMENSIONS;

/** Every lowercase token a faceted setup should match on: its name words, family, and variant values. */
function haystack(setup: FacetedSetup): string[] {
  const tokens = setup.name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  tokens.push(setup.source.toLowerCase());
  for (const axis of AXIS_KEYS) {
    const value = setup.variant[axis];
    if (value) {
      tokens.push(value);
      // a "v3"/"v6" alias so a TheCore edition matches the way players write it
      if (axis === "edition" && /^\d+$/.test(value)) tokens.push(`v${value}`);
    }
  }
  return tokens;
}

/**
 * A flat ranked result list for a query: split into tokens, a candidate matches only when every token
 * is a substring of some haystack token, ranked by how many tokens hit a whole token exactly (so a
 * fully-qualified query surfaces its one entry first). Faceted setups match on name + family + variant
 * tokens; flat members match on their name alone.
 */
export function searchSetups(
  faceted: readonly FacetedSetup[],
  flat: readonly FlatMember[],
  query: string,
): SearchHit[] {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const scored: { hit: SearchHit; score: number }[] = [];

  const consider = (id: string, name: string, hay: string[], tags: string[]) => {
    let score = 0;
    for (const token of tokens) {
      const exact = hay.includes(token);
      const partial = exact || hay.some((h) => h.includes(token));
      if (!partial) return; // every token must match somewhere, or the candidate is out
      if (exact) score += 1;
    }
    scored.push({ hit: { id, name, tags }, score });
  };

  for (const setup of faceted) {
    const tags = AXIS_KEYS.flatMap((axis) => {
      const value = setup.variant[axis];
      return value ? [axisOptionLabel(axis, value)] : [];
    });
    consider(setup.id, setup.name, haystack(setup), tags);
  }
  for (const member of flat) {
    consider(
      member.id,
      member.name,
      member.name
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean),
      [],
    );
  }

  return scored
    .sort((a, b) => b.score - a.score || a.hit.name.localeCompare(b.hit.name))
    .map((s) => s.hit);
}
